import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { MessageCircle, Send, Trash2, AtSign, X } from "lucide-react";
import { toast } from "sonner";

let _mentionableCache = null;

export default function HighlightComments({ highlight, onChanged }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [people, setPeople] = useState(_mentionableCache || []);
  const [busy, setBusy] = useState(false);
  const comments = highlight.comments || [];

  useEffect(() => {
    if (_mentionableCache) return;
    api.get("/users/mentionable").then((r) => { _mentionableCache = r.data; setPeople(r.data); }).catch(() => {});
  }, []);

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/highlights/${highlight.id}/comments`, { text, mentions: mentions.map((m) => m.id) });
      setText(""); setMentions([]); onChanged();
      if (mentions.length) toast.success(`Notified ${mentions.map((m) => m.name.split(" ")[0]).join(", ")}`);
    } catch { toast.error("Could not add comment"); }
    finally { setBusy(false); }
  };

  const remove = async (cid) => {
    try { await api.delete(`/highlights/${highlight.id}/comments/${cid}`); onChanged(); }
    catch { toast.error("Could not delete comment"); }
  };

  const toggleMention = (p) => setMentions((m) => (m.some((x) => x.id === p.id) ? m.filter((x) => x.id !== p.id) : [...m, p]));

  return (
    <div data-testid={`comments-${highlight.id}`} className="mt-3 pt-3 border-t border-slate-100">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1 mb-2"><MessageCircle className="w-3 h-3" /> Comments ({comments.length})</div>
      <div className="space-y-2">
        {comments.map((c) => (
          <div key={c.id} data-testid={`comment-${c.id}`} className={`text-sm p-2.5 rounded-lg flex gap-2 ${c.role === "manager" ? "bg-[#FFF7E8] border border-[#F5A623]/40" : "bg-slate-50"}`}>
            <div className="flex-1">
              <div className="text-xs text-slate-500"><span className="font-semibold text-[#0A2A5E]">{c.user_name}</span> · {c.role === "manager" ? "HR Manager" : "Employee"} · {new Date(c.created_at).toLocaleString()}</div>
              <div className="text-slate-800 mt-0.5">
                {(c.mentions || []).map((m) => <span key={m.id} data-testid={`mention-chip-${m.id}`} className="inline-flex items-center text-[11px] font-semibold text-[#0A2A5E] bg-[#DBEAFE] rounded px-1.5 py-0.5 mr-1">@{m.name}</span>)}
                {c.text}
              </div>
            </div>
            {(user?.role === "manager" || c.user_id === user?.id) && (
              <button data-testid={`delete-comment-${c.id}`} onClick={() => remove(c.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
            )}
          </div>
        ))}
      </div>
      {mentions.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {mentions.map((m) => (
            <span key={m.id} data-testid={`pending-mention-${m.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0A2A5E] bg-[#DBEAFE] rounded-full px-2 py-0.5">
              @{m.name} <button onClick={() => toggleMention(m)} className="hover:text-rose-600"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2 mt-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button data-testid={`mention-btn-${highlight.id}`} size="sm" variant="outline" className="h-9 px-2.5 border-slate-300" title="Tag a team member"><AtSign className="w-4 h-4" /></Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-2">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-1 mb-1">Tag someone (they'll be notified)</div>
            <div className="max-h-56 overflow-y-auto">
              {people.length === 0 && <div className="text-xs text-slate-500 p-2">No one to tag yet.</div>}
              {people.map((p) => {
                const on = mentions.some((x) => x.id === p.id);
                return (
                  <button key={p.id} data-testid={`mention-option-${p.id}`} onClick={() => toggleMention(p)} className={`w-full text-left text-sm px-2 py-1.5 rounded-md flex items-center justify-between ${on ? "bg-[#DBEAFE] text-[#0A2A5E]" : "hover:bg-slate-100"}`}>
                    <span>{p.name}</span><span className="text-[10px] uppercase text-slate-400">{p.role}</span>
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
        <Input data-testid={`comment-input-${highlight.id}`} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={user?.role === "manager" ? "Add coaching note or feedback…" : "Reply to your manager…"} className="h-9 text-sm" />
        <Button data-testid={`comment-send-${highlight.id}`} size="sm" onClick={add} disabled={busy || !text.trim()} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white h-9"><Send className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  );
}
